import { Fragment, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Code2,
  Building2,
  Smartphone,
  LayoutTemplate,
  Link2,
  RefreshCw,
  Shield,
  Users,
  Sparkles,
  HeadphonesIcon,
  MessageSquare,
} from "lucide-react";
import FAQSection from "../components/FAQSection";
import BlogSection from "../components/BlogSection";
import { AnimatedCard } from "../components/AnimatedCard";

const CALENDLY = "https://calendly.com/nvseeds/30min";

function RollingStatValue({ value, suffix = "", start }) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!start) return;

    let rafId = 0;
    const duration = 1300;
    const startTime = performance.now();

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(value * eased));
      if (progress < 1) rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [start, value]);

  const baseSuffix = suffix.endsWith("+") ? suffix.slice(0, -1) : suffix;
  const hasPlus = suffix.endsWith("+");

  return (
    <p className="text-2xl md:text-3xl font-bold">
      {displayValue}
      {baseSuffix && <span>{baseSuffix}</span>}
      {hasPlus && <span className="text-blue-400">+</span>}
    </p>
  );
}

export const ApplicationDevelopment = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "Application Development Services",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const appDevelopmentFaqs = [
    {
      question: "How much does custom app development cost in 2026?",
      answer:
        "Cost depends on complexity, but should be evaluated through total cost of ownership. Many custom solutions pay for themselves in 18 months by reducing licensing fees and improving operations.",
    },
    {
      question: "Can you work with our existing in-house team?",
      answer:
        "Absolutely. Many clients augment their internal teams with our specialists, or assign dedicated teams for specific product branches.",
    },
    {
      question: "How do you handle security and data privacy?",
      answer:
        "We implement SOC2-compliant processes and ensure development aligns with regional requirements like GDPR and CCPA. Security is a core feature, not an afterthought.",
    },
    {
      question: "Why choose NV Seeds over a local boutique agency?",
      answer:
        "Scale and stability. With 500+ projects and operations across 20+ countries, we ensure momentum and continuity.",
    },
  ];

  const stats = [
    { label: "Projects delivered", value: "500+", sub: "Proven delivery record" },
    { label: "Global reach", value: "20+", sub: "Countries served" },
    { label: "Client satisfaction", value: "98%", sub: "CSAT-focused teams" },
    { label: "Speed to market", value: "40%", sub: "Faster vs industry avg." },
  ];

  const phases = [
    {
      step: "01",
      phaseLabel: "PHASE 1",
      title: "Traditional Development",
      desc: "Monolithic apps, long release cycles, and infrastructure managed in-house.",
      icon: Code2,
      accent: "text-[#F59E0B]",
      iconBg: "bg-[#FEF3C7] border-[#FDE68A]",
      cardBg: "bg-white/90",
      dividerClass: "bg-[#F59E0B]",
      textMuted: "text-[#64748B]",
    },
    {
      step: "02",
      phaseLabel: "PHASE 2",
      title: "Cloud Adoption",
      desc: "Move to the cloud, embrace SaaS, and modernize infrastructure.",
      icon: Building2,
      accent: "text-[#3B82F6]",
      iconBg: "bg-blue-50 border-blue-200",
      cardBg: "bg-white/90",
      dividerClass: "bg-[#3B82F6]",
      textMuted: "text-[#64748B]",
    },
    {
      step: "03",
      phaseLabel: "PHASE 3",
      title: "Intelligent Transformation",
      desc: "AI-powered experiences, data-driven decisions, and agile teams delivering continuous business value.",
      icon: Sparkles,
      accent: "text-[#93C5FD]",
      iconBg: "bg-[#0B2A4A] border-[#1D4ED8]/30",
      cardBg: "bg-[#0B2A4A]",
      dividerClass: "bg-[#93C5FD]",
      textMuted: "text-blue-100/90",
      textPrimary: "text-white",
    },
  ];

  const serviceCards = [
    {
      icon: Code2,
      title: "Custom application development",
      body:
        "Proprietary tools that match how you work — scalability for 10x growth, zero-trust security, and interoperability with ERP, CRM, and legacy databases.",
    },
    {
      icon: Building2,
      title: "Enterprise application development",
      body:
        "Consolidate fragmented workflows into high-performance platforms. SAP ECC modernization, bespoke SaaS, and compliance-aligned delivery.",
    },
    {
      icon: Smartphone,
      title: "Cross-platform & mobile",
      body:
        "Context-first experiences across desktop, mobile, and wearables with native-quality performance using modern frameworks.",
      link: "/mobile-app-development",
      linkLabel: "Mobile capabilities",
    },
    {
      icon: LayoutTemplate,
      title: "Web applications",
      body:
        "Complex business logic in fast, reliable web environments — from internal ops to customer-facing products.",
      link: "/web-development",
      linkLabel: "Web development",
    },
    {
      icon: Link2,
      title: "APIs & integration",
      body:
        "Systems that talk to each other — API-first design so your custom apps connect to the rest of your estate without brittle glue code.",
    },
    {
      icon: RefreshCw,
      title: "Legacy modernization",
      body:
        "Extend the life of critical applications, reduce technical debt, and migrate safely toward modular, cloud-ready architectures.",
    },
  ];

  const processSteps = [
    { step: "01", title: "Consultation", desc: "Goals, constraints, and challenges — a shared vision for the build." },
    { step: "02", title: "Planning & design", desc: "Architecture, UX, and integration blueprint before code hardens." },
    { step: "03", title: "Development", desc: "Agile delivery with flexibility, transparency, and steady milestones." },
    { step: "04", title: "Testing & QA", desc: "Rigorous quality, performance, and security validation before release." },
    { step: "05", title: "Launch & support", desc: "Go-live, monitoring, and ongoing maintenance as you scale." },
  ];

  const whyUs = [
    { icon: Sparkles, title: "Proven expertise", text: "500+ projects and a delivery model that removes the black box of software engineering." },
    { icon: Users, title: "Skilled team", text: "Senior architects and specialists for strategic oversight and deep execution." },
    { icon: Shield, title: "Security & trust", text: "SOC2-aligned practices, GDPR/CCPA awareness, security as a core feature." },
    { icon: HeadphonesIcon, title: "End-to-end partnership", text: "From strategy through DevOps and monitoring — not a one-off handoff." },
  ];

  const trackRecordStats = [
    { value: 500, suffix: "+", label: "Projects delivered" },
    { value: 20, suffix: "+", label: "Global reach" },
    { value: 98, suffix: "%", label: "Client satisfaction" },
    { value: 40, suffix: "%", label: "Speed to market" },
  ];

  const trackRecordRef = useRef(null);
  const panelRef = useRef(null);
  const panelInView = useInView(panelRef, { once: true, amount: 0.3 });
  const { scrollYProgress } = useScroll({
    target: trackRecordRef,
    offset: ["start 85%", "end 15%"],
  });
  const [panelShift, setPanelShift] = useState(220);

  useEffect(() => {
    const measure = () => {
      const trackEl = trackRecordRef.current;
      const panelEl = panelRef.current;
      if (!trackEl || !panelEl) return;

      const trackWidth = trackEl.getBoundingClientRect().width;
      const panelWidth = panelEl.getBoundingClientRect().width;
      const shift = trackWidth / 2 - panelWidth / 2;
      setPanelShift(Math.max(0, shift));
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const panelX = useTransform(scrollYProgress, [0, 1], [0, panelShift]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        subject: "Application Development Services",
        message: "",
      });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error("Error submitting contact form:", error);
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>Custom Software & Application Development | Novaseeds Technologies</title>
        <meta
          name="description"
          content="NV Seeds delivers bespoke, secure, and scalable custom software and application development — from enterprise platforms to mobile and integrations."
        />
      </Helmet>

      <div className="min-h-screen pt-28 pb-16 md:pb-24 bg-background">
        <section className="relative overflow-hidden bg-gradient-to-b from-[#F8FAFC] via-background to-background">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(37,99,235,0.12),transparent)] pointer-events-none" />
          <div className="container relative mx-auto px-4 md:px-6 max-w-7xl pt-10 pb-14 md:pt-14 md:pb-20">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Services · Applications
            </p>
            <div className="max-w-4xl">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight leading-[1.08]">
                Custom{" "}
                <span className="text-gradient">Software Development</span>
              </h1>
              <p className="mt-5 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                We leverage modern technologies and platforms to deliver cutting-edge
                custom applications — secure, scalable, and aligned with how your business
                runs in 2026.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] transition hover:bg-blue-700"
                >
                  Connect With Us
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={CALENDLY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#CBD5E1] bg-card px-7 py-3.5 text-sm font-semibold text-foreground shadow-sm transition hover:bg-[#F8FAFC]"
                >
                  Book An Appointment
                </a>
              </div>
            </div>

          </div>
        </section>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl space-y-16 md:space-y-20">
          <section ref={trackRecordRef}>
            <div className="relative rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
              <img
                src="/images/application-development/our-track-record.png"
                alt="Team collaboration scene representing project delivery success"
                className="w-full h-[560px] md:h-[620px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#020617]/55 via-[#0f172a]/40 to-[#020617]/20" />

              <div className="absolute inset-0 flex items-start md:items-center justify-center p-5 md:p-8 lg:p-10">
                <motion.div
                  ref={panelRef}
                  style={{ x: panelX }}
                  className="w-full md:w-1/2 max-w-none rounded-3xl border border-white/20 bg-[#0F172A]/48 backdrop-blur-sm p-5 md:p-6 text-white"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-blue-100 mb-2">
                    Our Track Record
                  </p>
                  <h3 className="text-2xl md:text-4xl font-bold leading-tight">
                    Client-Specific.
                    <br />
                    Timely Delivery
                  </h3>
                  <p className="mt-4 text-sm md:text-base text-slate-100/90 leading-relaxed">
                    With our dynamic expertise across various platforms, we deliver
                    high-performing software solutions with quality and speed.
                  </p>

                  <div className="mt-6 grid grid-cols-2 gap-x-5 gap-y-4">
                    {trackRecordStats.map((item) => (
                      <div key={item.label}>
                        <RollingStatValue value={item.value} suffix={item.suffix} start={panelInView} />
                        <p className="text-xs md:text-sm text-slate-100/90">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          <section className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start pt-4">
            <div className="lg:col-span-7 space-y-5">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Your stack is either an engine or an anchor
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                Most businesses in 2026 are still running on software that was
                &quot;cutting-edge&quot; in 2020. That is a liability. If your current stack
                is just a collection of siloed tools rather than a cohesive ecosystem,
                you are not just behind: you are losing money every hour.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                At NV Seeds, we do not do &quot;generic.&quot; We build custom application
                development solutions that act as the central nervous system for your
                business. Whether you are scaling a startup or modernizing a legacy giant,
                your strategy should be an ROI driver, not a line-item expense.
              </p>
            </div>
            <AnimatedCard
              index={0}
              className="lg:col-span-5 rounded-3xl border border-[#E2E8F0] bg-card p-6 md:p-8 shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
            >
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-4">
                Why NV Seeds
              </p>
              <ul className="space-y-4">
                {[
                  "Intelligent ecosystems — not one-off apps in a vacuum",
                  "Clean core philosophy with AI and real-time data in scope",
                  "Strategic partner mindset: bottom-line impact per feature",
                ].map((t) => (
                  <li key={t} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-600 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </AnimatedCard>
          </section>

          <section>
            <div className="relative overflow-hidden rounded-3xl border border-[#E2E8F0] bg-[#F8FAFC]">
              <img
                src="/images/application-development/2026-development.png"
                alt="2026 development phases overview"
                className="w-full h-[520px] md:h-[560px] object-cover object-right"
              />

              {/* Left readability layer */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#F8FAFC] via-[#F8FAFC]/60 to-transparent pointer-events-none" />

              {/* Overlay content */}
              <div className="absolute inset-0 flex items-start md:items-center">
                <div className="w-full px-4 md:px-8 pt-10 md:pt-0 md:pl-8">
                  <div className="max-w-2xl">
                    <h2 className="text-3xl md:text-5xl font-semibold text-foreground tracking-tight leading-[1.1]">
                      The 2026 state of play
                    </h2>
                    <p className="mt-3 text-muted-foreground text-base md:text-lg">
                      Three phases — enterprise application development today lives in the third.
                    </p>
                    <div className="mt-4 h-[2px] w-16 bg-[#F59E0B]/70 rounded-full" />
                  </div>

                  <div className="mt-6 md:mt-10 flex items-stretch gap-6 md:gap-10 max-w-5xl">
                    {phases.map((p, i) => {
                      const Icon = p.icon;
                      return (
                        <Fragment key={p.title}>
                          <AnimatedCard
                            index={i}
                            slideFrom="right"
                            enableHover={false}
                            hoverScale={1}
                            hoverLift={0}
                            className={`w-[240px] md:w-[260px] flex-none rounded-2xl border transition-colors ${
                              i === 2 ? "border-[#1D4ED8]/30" : "border-[#E2E8F0]"
                            }`}
                          >
                            <div className={`h-full rounded-2xl p-5 md:p-6 ${p.cardBg}`}>
                              <div className="flex items-start gap-3">
                                <div
                                  className={`h-12 w-12 rounded-full border flex items-center justify-center ${p.iconBg}`}
                                >
                                  <Icon className={`h-6 w-6 ${p.accent}`} strokeWidth={1.75} />
                                </div>

                                <div className="pt-1">
                                  <p
                                    className={`text-sm font-semibold ${
                                      i === 2 ? "text-blue-100" : "text-muted-foreground"
                                    }`}
                                  >
                                    {p.step}
                                  </p>
                                  <p
                                    className={`text-xs font-semibold tracking-wider ${
                                      i === 2 ? "text-blue-100/90" : "text-muted-foreground"
                                    }`}
                                  >
                                    {p.phaseLabel}
                                  </p>
                                </div>
                              </div>

                              <p
                                className={`${
                                  i === 2 ? "text-white" : "text-foreground"
                                } mt-4 text-xl font-semibold`}
                              >
                                {p.title}
                              </p>
                              <div className={`mt-3 h-[2px] w-14 ${p.dividerClass}`} />

                              <p
                                className={`mt-4 text-sm md:text-base leading-relaxed ${
                                  i === 2 ? "text-blue-100/90" : p.textMuted
                                }`}
                              >
                                {p.desc}
                              </p>
                            </div>
                          </AnimatedCard>

                          {i === 0 && (
                            <div className="hidden md:flex items-center justify-center w-10 self-center">
                              <ArrowRight className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                          {i === 1 && (
                            <div className="hidden md:flex items-center justify-center w-10 self-center">
                              <ArrowRight className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                Our services
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Custom software development services
              </h2>
              <p className="mt-3 text-muted-foreground">
                End-to-end capabilities — from proprietary products to enterprise platforms and integrations.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-visible">
              {serviceCards.map(({ icon: Icon, title, body, link, linkLabel }, i) => (
                <AnimatedCard
                  key={title}
                  index={i}
                  slideFrom="alternate"
                  hoverScale={1.04}
                  hoverLift={-10}
                  className="group rounded-3xl border border-[#E2E8F0] bg-card p-8 shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:shadow-xl hover:border-blue-200/60"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 border border-blue-100 text-blue-600 mb-5 transition-colors group-hover:bg-blue-100/80 group-hover:scale-110 duration-300">
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{title}</h3>
                  <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">{body}</p>
                  {link && linkLabel && (
                    <Link
                      to={link}
                      className="inline-flex mt-4 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {linkLabel}
                    </Link>
                  )}
                </AnimatedCard>
              ))}
            </div>
          </section>

          <section>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                Our process
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                How we develop high-performing software
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 overflow-visible">
              {processSteps.map((s, i) => (
                <AnimatedCard
                  key={s.step}
                  index={i}
                  className="relative rounded-3xl border border-[#E2E8F0] bg-gradient-to-b from-card to-[#F8FAFC]/50 p-6 md:p-7"
                >
                  <span className="text-3xl font-bold text-blue-600/25">{s.step}</span>
                  <h3 className="mt-2 text-base font-semibold text-foreground">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </AnimatedCard>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-[#E2E8F0] bg-[#F8FAFC] p-8 md:p-12">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Why work with NV Seeds
              </h2>
              <p className="mt-3 text-muted-foreground">
                Best-in-class delivery with a client-centric model — built for long-term success.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 overflow-visible">
              {whyUs.map(({ icon: Icon, title, text }, i) => (
                <AnimatedCard
                  key={title}
                  index={i}
                  className="group rounded-2xl bg-card border border-[#E2E8F0] p-6 shadow-sm"
                >
                  <Icon className="h-8 w-8 text-blue-600 mb-3 transition-transform duration-300 group-hover:scale-110" strokeWidth={1.5} />
                  <h3 className="font-semibold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
                </AnimatedCard>
              ))}
            </div>
          </section>

          <AnimatedCard
            index={0}
            hoverScale={1.012}
            hoverLift={-4}
            className="rounded-3xl border border-[#E2E8F0] bg-card p-6 md:p-10 shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
              Proof in the metrics
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              We do not just write code; we solve business problems. Our track record speaks for itself.
            </p>
            <div className="overflow-x-auto rounded-xl border border-[#E2E8F0]">
              <table className="w-full text-sm md:text-base">
                <thead className="bg-[#F8FAFC]">
                  <tr>
                    <th className="text-left font-semibold text-foreground px-4 py-3 border-b border-[#E2E8F0]">Metric</th>
                    <th className="text-left font-semibold text-foreground px-4 py-3 border-b border-[#E2E8F0]">Achievement</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Successful projects</td>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">500+</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Global reach</td>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">20+ countries</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Client satisfaction</td>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">98% (CSAT)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Average speed to market</td>
                    <td className="px-4 py-3">40% faster than industry standard</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground leading-relaxed mt-6">
              When you work with us, you are gaining a strategic partner that understands the
              bottom-line impact of every feature.
            </p>
          </AnimatedCard>

          <AnimatedCard index={0} className="rounded-3xl border border-[#E2E8F0] bg-card p-8 md:p-10 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
              The economics of custom software: build vs. buy in 2026
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              The &quot;Buy&quot; option is tempting because of the lower upfront cost. However, the
              &quot;Build&quot; option, when done correctly, is almost always the superior financial move
              for long-term growth.
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Zero licensing fees: stop paying per-user monthly taxes to third-party vendors.</li>
              <li>Ownership of data: in an AI-driven economy, your data is your most valuable asset.</li>
              <li>Process alignment: software evolves to fit your business, not the other way around.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              Subscription fatigue is real. By the time you integrate multiple SaaS tools to do one job,
              you may have spent enough to build a custom powerhouse twice over.
            </p>
          </AnimatedCard>

          <AnimatedCard
            index={0}
            hoverScale={1}
            hoverLift={-8}
            className="grid lg:grid-cols-12 gap-8 items-stretch rounded-3xl border border-[#E2E8F0] bg-card overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
          >
            <div className="lg:col-span-7 p-8 md:p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-[#E2E8F0]">
              <p className="text-sm font-semibold text-blue-600 mb-2">Case study</p>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-6">
                Modernizing global logistics for 2026
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-2">
                <span className="font-medium text-foreground">The client:</span> A Tier-1 logistics
                provider operating across 12 countries.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-2">
                <span className="font-medium text-foreground">The problem:</span> Legacy enterprise
                applications from 2018, spreadsheet-heavy dispatch, and a clunky mobile app with no
                real-time sync.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                <span className="font-medium text-foreground">The solution:</span> NV Seeds built a
                unified AI-enhanced coordination platform with real-time traffic integration,
                predictive maintenance alerts, and a custom field mobile interface.
              </p>
              <p className="font-medium text-foreground mb-2">The result</p>
              <ul className="space-y-2 text-muted-foreground mb-8">
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  22% reduction in fuel costs through optimized routing
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  99.9% uptime during peak holiday seasons
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  100% elimination of manual data entry errors
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  Transition from manual logbooks to streamlined custom software
                </li>
              </ul>
              <img
                src="/images/application-development/logistics-tablet-dashboard.png"
                alt="Operations tablet dashboard used for logistics route and shipment tracking"
                className="w-full rounded-2xl border border-[#E2E8F0]"
              />
            </div>
            <div className="lg:col-span-5 flex flex-col bg-gradient-to-br from-blue-600 to-blue-800 p-8 md:p-10 text-white min-h-full">
              <MessageSquare className="h-10 w-10 opacity-90 mb-4" />
              <p className="text-lg font-semibold leading-snug">Your project playbook</p>
              <ul className="mt-6 space-y-3 text-sm text-blue-100 leading-relaxed">
                <li>· Strategy audit: high-impact opportunities first</li>
                <li>· Blueprint integrations before coding accelerates</li>
                <li>· Agile sprints: visible progress every two weeks</li>
                <li>· DevOps, hosting, and monitoring included</li>
              </ul>
            </div>
          </AnimatedCard>

          <AnimatedCard index={0} className="rounded-3xl border border-[#E2E8F0] bg-card p-8 md:p-10 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
              The human element in tech
            </h2>
            <div className="grid md:grid-cols-12 gap-6 items-start">
              <p className="md:col-span-8 text-muted-foreground leading-relaxed">
                Behind every line of code is a team of experts who care about your success. Our culture
                at NV Seeds is built on collaboration and transparency. We do not hide behind Jira
                tickets; we embed ourselves in your goals.
              </p>
              <div className="md:col-span-4">
                <img
                  src="/images/application-development/team-collaboration.png"
                  alt="Diverse software development team collaborating in a planning meeting"
                  className="w-full rounded-2xl border border-[#E2E8F0]"
                />
              </div>
            </div>
          </AnimatedCard>

          <AnimatedCard
            index={0}
            hoverScale={1.02}
            hoverLift={-6}
            className="rounded-3xl border border-blue-200 bg-gradient-to-r from-blue-50 to-[#EFF6FF] px-6 py-10 md:px-12 md:py-14 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground">
              Ready to build your custom software?
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              In 2026, your software is either an engine for growth or an anchor. Let us help you build
              an appreciating asset.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] hover:bg-blue-700"
              >
                Book An Appointment
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-[#CBD5E1] bg-white px-8 py-3.5 text-sm font-semibold text-foreground hover:bg-[#F8FAFC]"
              >
                Contact us
              </Link>
            </div>
          </AnimatedCard>

          <section id="contact" className="overflow-visible">
            <AnimatedCard index={0} hoverScale={1.015} hoverLift={-4} className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 md:p-10 rounded-3xl">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-3">
                Contact us
              </h2>
              <p className="text-muted-foreground mb-8">
                Tell us about your application development goals and our team will get back to you shortly.
              </p>

              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle2 size={40} className="text-green-600 mx-auto mb-3" />
                  <p className="text-muted-foreground">Thank you! We have received your message.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your Name"
                      className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none w-full"
                    />
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Email Address"
                      className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none w-full"
                    />
                  </div>

                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Subject"
                    className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none w-full mb-4"
                  />

                  <textarea
                    rows={5}
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your project requirements..."
                    className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none w-full resize-none mb-6"
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-7 py-3 font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.25)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending..." : "Send Message"}
                    {!loading && <ArrowRight size={18} />}
                  </button>
                </form>
              )}
            </AnimatedCard>
          </section>

          <BlogSection
            categorySlug="software-development-company"
            title="What's New In The Market"
            maxPosts={3}
          />
          <FAQSection
            faqs={appDevelopmentFaqs}
            title="FAQ: What You Need to Know About App Development"
          />
        </div>
      </div>
    </>
  );
};
